import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import {
  pb,
  messageOf,
  newRecordId,
  type InvitePreviewResponse,
  type Json,
  type PartyInviteRecord,
  type PartyMemberRecord,
  type PartyRecord,
  type PartySubscriptionRecord,
} from '@/shared/api'
import { useAuthStore } from '@/shared/session'
import { useContentStore, type PackPointer } from '@/entities/content'

import type { InvitePreview, Party, PartyInvite, PartyMember, PartyPackSubscription } from '../types/party'
import { toParty, toPartyInvite, toPartyMember, toPartySubscription } from '../lib/partyMappers'

const currentUserId = (): string | null => useAuthStore.getState().user?.id ?? null
const currentEmail = (): string | null => useAuthStore.getState().user?.email?.toLowerCase() ?? null

/** Options for a shareable link invite. */
export interface LinkInviteOptions {
  maxUses?: number
  expiresAt?: string
}

interface PartyStore {
  /** Parties the user is a member of (owner included). */
  parties: Party[]
  /** Roster per party (members + resolved usernames). */
  membersByParty: Record<string, PartyMember[]>
  /** Every party subscription pointer across all joined parties. */
  subscriptions: PartyPackSubscription[]
  /** Pending email invites addressed to the current user (the accept/decline banner). */
  incomingInvites: PartyInvite[]
  loaded: boolean
  error: string | null

  /** Refresh all party state from the server (the source of truth). No-op when signed out. */
  loadFromCloud: () => Promise<void>
  /** Drop all party state (on sign-out). */
  clear: () => void
  /** Subscribe to live party changes so a DM's edits propagate at once. */
  startRealtime: () => void
  /** Tear down the realtime subscriptions. */
  stopRealtime: () => void

  createParty: (name: string) => Promise<string | null> // returns new party id, or null on failure
  renameParty: (partyId: string, name: string) => Promise<string | null>
  setMembersCanSeeCharacters: (partyId: string, value: boolean) => Promise<string | null>
  disbandParty: (partyId: string) => Promise<string | null>

  addSubscription: (partyId: string, pointer: PackPointer) => Promise<string | null>
  removeSubscription: (subscriptionId: string) => Promise<string | null>

  inviteByEmail: (partyId: string, email: string) => Promise<string | null>
  createLinkInvite: (partyId: string, opts?: LinkInviteOptions) => Promise<string | null> // returns code
  revokeInvite: (inviteId: string) => Promise<string | null>
  previewInvite: (code: string) => Promise<InvitePreview | null>
  acceptInvite: (inviteId: string) => Promise<string | null>
  declineInvite: (inviteId: string) => Promise<string | null>
  redeemCode: (code: string) => Promise<string | null> // returns party id

  leaveParty: (partyId: string) => Promise<string | null>
  removeMember: (partyId: string, userId: string) => Promise<string | null>
  transferOwnership: (partyId: string, newOwnerId: string) => Promise<string | null>
  setMyCharacter: (partyId: string, characterId: string | null) => Promise<string | null>
}

/**
 * Every mutator reports the same way the Supabase ones did: null on success, a
 * readable message otherwise, and the state reloaded either way. `messageOf`
 * digs the reason out of PocketBase's field errors, whose top-level message is
 * only ever "Failed to update record."
 */
async function act(work: () => Promise<unknown>, reload: () => Promise<void>): Promise<string | null> {
  try {
    await work()
    await reload()
    return null
  } catch (error) {
    await reload()
    return messageOf(error)
  }
}

/**
 * The four collections that make a party, and a small debounce so a burst of
 * record changes triggers one reload.
 *
 * Realtime respects the same read rules as a request, so a client is only told
 * about records it may see, exactly as the Postgres publication did.
 */
const LIVE = ['parties', 'party_members', 'party_invites', 'party_subscriptions'] as const
let unsubscribers: (() => void)[] = []
let refreshTimer: ReturnType<typeof setTimeout> | null = null
/**
 * Guards against subscribing twice, and it has to be a plain synchronous flag.
 *
 * `pb.collection(...).subscribe(...)` is async (it resolves once the socket is
 * open) and only pushes its unsubscribe function onto `unsubscribers` once that
 * promise settles. The Supabase client this replaces was synchronous —
 * `channel()` returned immediately — so guarding on `unsubscribers.length` used
 * to work. Under PocketBase, two `startRealtime()` calls made back to back (no
 * await in between, which is how `App.tsx` calls it) would both see an empty
 * array and both subscribe, doubling every collection's listeners.
 */
let realtimeStarting = false

export const usePartyStore = create<PartyStore>()(
  persist(
    (set, get) => ({
      parties: [],
      membersByParty: {},
      subscriptions: [],
      incomingInvites: [],
      loaded: false,
      error: null,

      loadFromCloud: async () => {
        const uid = currentUserId()
        if (!uid) return
        try {
          // What comes back is decided by the rules, not by a filter: a member
          // sees their own parties and nothing else. `expand` on the roster is
          // what replaces the separate `profiles` query, since the username now
          // lives on the user record.
          const [parties, memberRecords, subs, inviteRecords] = await Promise.all([
            pb.collection('parties').getFullList<PartyRecord>(),
            pb.collection('party_members').getFullList<PartyMemberRecord>({ expand: 'user' }),
            pb.collection('party_subscriptions').getFullList<PartySubscriptionRecord>(),
            pb.collection('party_invites').getFullList<PartyInviteRecord>({
              filter: pb.filter('status = {:status}', { status: 'pending' }),
              expand: 'invited_by',
            }),
          ])

          const membersByParty: Record<string, PartyMember[]> = {}
          for (const record of memberRecords) {
            const member = toPartyMember(record)
            ;(membersByParty[member.partyId] ??= []).push(member)
          }

          const email = currentEmail()
          const invites = inviteRecords.map(toPartyInvite)
          const incoming = invites.filter(
            (i) => i.kind === 'email' && i.email?.toLowerCase() === email && i.invitedBy !== uid,
          )

          const mapped = parties.map(toParty)
          const subscriptions = subs.map(toPartySubscription)
          set({
            parties: mapped,
            membersByParty,
            subscriptions,
            incomingInvites: incoming,
            loaded: true,
            error: null,
          })

          // Feed inherited pointers into the content pipeline (read-only, party-scoped).
          const partyName = new Map(mapped.map((p) => [p.id, p.name]))
          useContentStore.getState().setPartyPointers(
            subscriptions.map((s) => ({
              pointer: s.pointer,
              scope: { kind: 'party' as const, partyId: s.partyId, partyName: partyName.get(s.partyId) ?? 'Party' },
            })),
          )
        } catch (error) {
          set({ loaded: true, error: messageOf(error) })
        }
      },

      clear: () => {
        set({ parties: [], membersByParty: {}, subscriptions: [], incomingInvites: [], loaded: false, error: null })
        useContentStore.getState().setPartyPointers([]) // evict inherited packs on sign-out
      },

      startRealtime: () => {
        if (realtimeStarting) return
        realtimeStarting = true
        const refresh = () => {
          if (refreshTimer) clearTimeout(refreshTimer)
          refreshTimer = setTimeout(() => void get().loadFromCloud(), 300)
        }
        for (const collection of LIVE) {
          // `subscribe` resolves to its own unsubscriber, so the promise is kept
          // rather than the topic name: unsubscribing by name would also tear
          // down a subscription somebody else had opened on the same collection.
          void pb
            .collection(collection)
            .subscribe('*', refresh)
            .then((off) => unsubscribers.push(off))
            .catch(() => {
              // A realtime stream that will not open costs liveness, not
              // correctness: every mutator reloads anyway.
            })
        }
      },

      stopRealtime: () => {
        realtimeStarting = false
        if (refreshTimer) {
          clearTimeout(refreshTimer)
          refreshTimer = null
        }
        for (const off of unsubscribers) off()
        unsubscribers = []
      },

      createParty: async (name) => {
        try {
          // A hook route, because a party and its first membership are one act.
          const created = await pb.send<{ id: string; name: string }>('/api/tomf/parties', {
            method: 'POST',
            body: { name },
          })
          await get().loadFromCloud()
          return created.id
        } catch (error) {
          set({ error: messageOf(error) })
          return null
        }
      },

      renameParty: (partyId, name) =>
        act(() => pb.collection('parties').update(partyId, { name }), get().loadFromCloud),

      setMembersCanSeeCharacters: (partyId, value) =>
        act(
          () => pb.collection('parties').update(partyId, { members_can_see_characters: value }),
          get().loadFromCloud,
        ),

      disbandParty: (partyId) =>
        act(() => pb.collection('parties').delete(partyId), get().loadFromCloud),

      addSubscription: (partyId, pointer) =>
        act(
          () =>
            pb.collection('party_subscriptions').create({
              party: partyId,
              pointer: pointer as unknown as Json,
              added_by: currentUserId(),
            }),
          get().loadFromCloud,
        ),

      removeSubscription: (subscriptionId) =>
        act(() => pb.collection('party_subscriptions').delete(subscriptionId), get().loadFromCloud),

      inviteByEmail: (partyId, email) =>
        act(
          () =>
            pb.collection('party_invites').create({
              party: partyId,
              invited_by: currentUserId(),
              kind: 'email',
              email,
              status: 'pending',
            }),
          get().loadFromCloud,
        ),

      createLinkInvite: async (partyId, opts) => {
        // The code is the capability, so it is generated where it can be handed
        // straight to the person creating it. A record id would be shorter and
        // is the wrong thing: it is guessable in the sense that it is also the
        // row's address.
        const code = newRecordId() + newRecordId()
        try {
          await pb.collection('party_invites').create({
            party: partyId,
            invited_by: currentUserId(),
            kind: 'link',
            code,
            status: 'pending',
            // 0 rather than null: PocketBase numbers have no NULL, and the hook
            // reads 0 as unlimited.
            max_uses: opts?.maxUses ?? 0,
            expires: opts?.expiresAt ?? '',
          })
        } catch (error) {
          set({ error: messageOf(error) })
          return null
        }
        await get().loadFromCloud()
        return code
      },

      revokeInvite: (inviteId) =>
        act(
          () => pb.collection('party_invites').update(inviteId, { status: 'revoked' }),
          get().loadFromCloud,
        ),

      previewInvite: async (code) => {
        try {
          // Unauthenticated on purpose: somebody following a link has to see
          // whose table it is before they have an account to see it with.
          const row = await pb.send<InvitePreviewResponse>(
            `/api/tomf/invites/preview?code=${encodeURIComponent(code)}`,
            { method: 'GET' },
          )
          return { partyId: row.party, partyName: row.name, inviter: row.inviter }
        } catch {
          // A bad, spent or revoked code is the ordinary answer here.
          return null
        }
      },

      acceptInvite: (inviteId) =>
        act(
          () => pb.send(`/api/tomf/invites/${inviteId}/accept`, { method: 'POST' }),
          get().loadFromCloud,
        ),

      declineInvite: (inviteId) =>
        act(
          () => pb.send(`/api/tomf/invites/${inviteId}/decline`, { method: 'POST' }),
          get().loadFromCloud,
        ),

      redeemCode: async (code) => {
        try {
          const row = await pb.send<{ party: string }>('/api/tomf/invites/redeem', {
            method: 'POST',
            body: { code },
          })
          await get().loadFromCloud()
          return row.party
        } catch (error) {
          set({ error: messageOf(error) })
          return null
        }
      },

      leaveParty: (partyId) => {
        const uid = currentUserId()
        if (!uid) return Promise.resolve('Not signed in')
        // Leaving and being removed are the same route: an owner may remove
        // anybody, anybody may remove themselves.
        return act(
          () => pb.send(`/api/tomf/parties/${partyId}/members/${uid}`, { method: 'DELETE' }),
          get().loadFromCloud,
        )
      },

      removeMember: (partyId, userId) =>
        act(
          () => pb.send(`/api/tomf/parties/${partyId}/members/${userId}`, { method: 'DELETE' }),
          get().loadFromCloud,
        ),

      transferOwnership: (partyId, newOwnerId) =>
        act(
          () =>
            pb.send(`/api/tomf/parties/${partyId}/owner`, {
              method: 'POST',
              body: { user: newOwnerId },
            }),
          get().loadFromCloud,
        ),

      setMyCharacter: (partyId, characterId) => {
        const uid = currentUserId()
        const membership = get().membersByParty[partyId]?.find((m) => m.userId === uid)
        if (!membership) return Promise.resolve('Not a member of this party')
        // '' unlinks: PocketBase relations have no NULL. What may change on a
        // membership, and whose character it may point at, is enforced by
        // `pb_hooks/party_members.pb.js` rather than by the update rule.
        return act(
          () => pb.collection('party_members').update(membership.id, { character: characterId ?? '' }),
          get().loadFromCloud,
        )
      },
    }),
    {
      name: 'dnd-parties',
      storage: createJSONStorage(() => localStorage),
      // Persist the cloud-derived state for offline inheritance; it's refreshed by
      // loadFromCloud() on next sign-in. Invites/loaded/error are session-only.
      partialize: (state) => ({
        parties: state.parties,
        membersByParty: state.membersByParty,
        subscriptions: state.subscriptions,
      }),
    },
  ),
)
